import React, {
  useEffect,
  useState,
  createContext,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import { GetStarted } from '../components/GetStarted';
import { PostLink } from '../components/PostLink';
import { useStore } from '../../../store';
import { TEXT, STYLE } from '../constants';
import { VIEWS_MAP } from '../../../router/constants';
import { useSetLinkAccount } from '../../../hooks/query/useSetLinkAccount';
import { useGetLinkedAccountsQuery } from '../../../hooks/query/useGetLinkedAccountsQuery';
import { ILinkAccountProps, ILinkedAccount, IPostLinkData } from '../../../types';
import SubContent from '../../../generic-components/Layout/SubContent';
import { MoneyTransfer } from '../components/MoneyTransfer/MoneyTransfer';
import { PlaidFlowSucceed } from '../components/PlaidFlowSucceed';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { Layout } from '../../../components/Layout/Layout';
import Heading from '../../../generic-components/Heading';

const { PAGES: { INDEX } } = STYLE;
const { PAGES: { INDEX: { LAYOUT: { HEADER, HEADER_SEND_MONEY } } } } = TEXT;

export const FlowContext = createContext<boolean | null>(null);
export const UploadContext = createContext<any>(null);

export const AddMoney = () => {
  const [linkAccountProps, setLinkAccountProps] = useState<ILinkAccountProps>({ publicToken: '', institutionId: '', metaDataStr: '' });
  const [accounts, setAccounts] = useState<ILinkedAccount[] | null>(null);
  const [activeAccount, setActiveAccount] = useState<{idx: number|null, title: string}>({ idx: null, title: '' });
  const [isFlowSucceed, setIsFlowSucceed] = useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<string>('');
  const [successViewData, setSuccessViewData] = useState<{date: string, amount: string, firstTimeMessage?: string}>({ date: '', amount: '' });
  const { customerId } = useStore(state => state.user);
  const { routerHistory } = useStore(state => state);
  const [isAddMoneyFlow, setIsAddMoneyFlow] = useState<boolean | null>(false);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const {
    data: updatedLinkedAccounts, refetch: getUpdatedLinkedAccounts, isRefetching: isFetchingLinkedAccounts, isLoading: isLoadingGetLinkedAccounts,
  } = useGetLinkedAccountsQuery(customerId);
  const {
    refetch: setLinkAccount, data: linkAccountsResponse, isRefetching: isFetchingSetLinkAccount, isLoading: isLoadingSetLinkAccount,
  } = useSetLinkAccount(
    customerId,
    linkAccountProps,
  );

  const handlePlaidMetaData = (data: IPostLinkData) => {
    if (data) {
      setLinkAccountProps(prevState => ({
        ...prevState,
        publicToken: data.publicToken,
        institutionId: data.metaData.institution!.institution_id,
        metaDataStr: JSON.stringify(data.metaData),
      }));
    }
  };

  const removeCheckingAccount = () => {
    getUpdatedLinkedAccounts();
  };

  const handleActiveLinkOption = (data: {activeItemIdx: number | null, displayTitle: string}) => {
    const { activeItemIdx, displayTitle } = data;
    setActiveAccount({ idx: activeItemIdx, title: displayTitle });
  };

  const handleSuccessTransfer = (estimatedArrivalDate: string, amountToTransfer: string, firstTimeMessage?: string) => {
    setSuccessViewData({ date: estimatedArrivalDate, amount: amountToTransfer, firstTimeMessage });
    setIsFlowSucceed(true);
  };

  const handleFinishFlow = () => {
    setIsFlowSucceed(false);
    setAccounts(null);
    navigate({ pathname: `/${VIEWS_MAP.DASHBOARD}` });
  };

  useEffect(() => {
    if (!updatedLinkedAccounts) return;
    const { accounts: accountsData } = updatedLinkedAccounts;
    if (accountsData && accountsData.length) {
      setAccounts(accountsData);
    } else {
      setAccounts(null);
    }
    setActiveAccount({ idx: null, title: '' });
  }, [updatedLinkedAccounts]);

  useEffect(() => {
    if (!linkAccountsResponse) return;
    getUpdatedLinkedAccounts();
  }, [linkAccountsResponse]);

  useEffect(() => {
    if (!linkAccountProps.publicToken) return;
    setLinkAccount();
  }, [linkAccountProps]);

  useEffect(() => {
    if (!pathname) return;
    const cleanPathName = pathname.replace('/', '');
    setIsAddMoneyFlow(cleanPathName === 'add-money');
  }, [pathname]);

  useEffect(() => {
    setAccounts(null);
    getUpdatedLinkedAccounts()
      .then(({ data }) => {
        if (!data) {
          setAccounts(null);
          return;
        }
        const { accounts: accountsData } = data;
        if (accountsData && accountsData.length) {
          setAccounts(accountsData);
        } else {
          setAccounts(null);
        }
        setActiveAccount({ idx: null, title: '' });
      });
  }, [isAddMoneyFlow]);

  useEffect(() => {
    trackMixPanel(
      'View Plaid Into Screen',
      {
        'Transaction Type': isAddMoneyFlow ? 'Add Money' : 'Send Money',
        Origin: routerHistory[routerHistory.length - 2] || 'Home',
      },
    );
    const cleanPathName = pathname.replace('/', '');
    setIsAddMoneyFlow(cleanPathName === 'add-money');
    getUpdatedLinkedAccounts();
  }, []);

  const uploadContextValue = useMemo(() => ({ transactionId, setTransactionId, setAccounts }), [transactionId]);

  return (
    <Layout wrapperClassName='pt-[78px] pl-[72px] w-screen pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <Heading title={isAddMoneyFlow ? t(HEADER) : t(HEADER_SEND_MONEY)} className='font-metricRSemibold text-[20px] text-bw08 mb-[24px]' />
      {isAddMoneyFlow !== null && (
        <FlowContext.Provider value={isAddMoneyFlow}>
          <UploadContext.Provider value={uploadContextValue}>
            <ContentWrapper>
              <MainContent className={isAddMoneyFlow ? INDEX.MAIN_CONTENT_NEW : INDEX.MAIN_CONTENT}>
                { (!accounts && !isFlowSucceed) && <GetStarted isFetchingLinkedAccounts={isFetchingLinkedAccounts || isFetchingSetLinkAccount || isLoadingSetLinkAccount || isLoadingGetLinkedAccounts} handlePlaidMetaData={handlePlaidMetaData} />}
                {(accounts && !isFlowSucceed) && (
                <PostLink
                  isFetchingLinkedAccounts={isFetchingLinkedAccounts || isFetchingSetLinkAccount || isLoadingSetLinkAccount || isLoadingGetLinkedAccounts}
                  handlePlaidMetaData={handlePlaidMetaData}
                  removeCheckingAccount={removeCheckingAccount}
                  metaData={accounts}
                  handleActiveLinkOption={handleActiveLinkOption}
                />
                ) }
                {isFlowSucceed && <PlaidFlowSucceed handleFinishFlow={handleFinishFlow} data={successViewData} activeLinkAccount={accounts && activeAccount.idx !== null ? accounts[activeAccount.idx] : undefined} />}
              </MainContent>
              {(activeAccount.idx !== null && accounts && !isFlowSucceed) && (
              <SubContent className={!isAddMoneyFlow ? INDEX.SUB_CONTENT_NEW : INDEX.SUB_CONTENT}>
                <MoneyTransfer handleSuccessTransfer={handleSuccessTransfer} activeLinkAccount={accounts[activeAccount.idx]} displayTitle={activeAccount.title} />
              </SubContent>
              )}
            </ContentWrapper>
          </UploadContext.Provider>
        </FlowContext.Provider>
      ) }
    </Layout>
  );
};
